import React from "react";
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
  Chip,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import MenuBookRoundedIcon from "@mui/icons-material/MenuBookRounded";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import TravelExploreOutlinedIcon from "@mui/icons-material/TravelExploreOutlined";
import CallSplitOutlinedIcon from "@mui/icons-material/CallSplitOutlined";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import DownloadDoneOutlinedIcon from "@mui/icons-material/DownloadDoneOutlined";
import SwapHorizOutlinedIcon from "@mui/icons-material/SwapHorizOutlined";
import RestorePageOutlinedIcon from "@mui/icons-material/RestorePageOutlined";

import ProcessFlow from "./ProcessFlow";
import Sidebar from "../Sidebar/Sidebar";
import useAuth from "../../hooks/useAuth";

// Jednoduché kroky – krátke vety, bez odborného žargónu
const VALIDATION_STEPS = [
  { Icon: UploadFileOutlinedIcon, title: "Vložte dokument", desc: "Nahrajte súbor (PDF alebo Word) do časti Validácia." },
  { Icon: TravelExploreOutlinedIcon, title: "Systém prehľadá", desc: "Program sám nájde osobné a citlivé údaje v dokumente." },
  { Icon: FactCheckOutlinedIcon, title: "Ukáže výsledok", desc: "Zobrazí, koľko citlivých údajov našiel a akého druhu." },
  { Icon: CallSplitOutlinedIcon, title: "Rozhodnete sa", desc: "Či dokument pošlete tak, alebo ho dáte najprv skryť." },
];

const ANON_STEPS = [
  { Icon: UploadFileOutlinedIcon, title: "Vložte súbory", desc: "Môžete naraz aj viac (PDF, Word, alebo obrázky)." },
  { Icon: TuneOutlinedIcon, title: "Vyberte, čo skryť", desc: "Zvoľte hotový profil alebo si zaškrtnite vlastné údaje." },
  { Icon: SearchOutlinedIcon, title: "Systém nájde údaje", desc: "Vyhľadá vybrané údaje aj v texte, aj na obrázkoch." },
  { Icon: VisibilityOffOutlinedIcon, title: "Začierni ich", desc: "Údaje natrvalo prekryje, takže ich už nikto neuvidí." },
  { Icon: DownloadDoneOutlinedIcon, title: "Stiahnete výsledok", desc: "Dostanete čistý dokument. Voliteľne sa uloží aj do aplikácie." },
];

const PSEUDO_STEPS = [
  { Icon: UploadFileOutlinedIcon, title: "Vložte Word", desc: "Nahrajte dokument vo formáte .docx." },
  { Icon: TuneOutlinedIcon, title: "Vyberte, čo nahradiť", desc: "Zaškrtnite údaje alebo zvoľte hotový profil." },
  { Icon: SwapHorizOutlinedIcon, title: "Nahradí za náhradné", desc: "Údaje vymení za zástupné (napr. meno za iné meno)." },
  { Icon: LockOutlinedIcon, title: "Zapamätá si zmeny", desc: "Bezpečne si uloží, čo za čo vymenil." },
  { Icon: RestorePageOutlinedIcon, title: "Viete to vrátiť", desc: "Kedykoľvek nahráte súbor späť a získate pôvodné údaje." },
];

// Plain-language porovnanie troch procesov
const COMPARE = [
  {
    title: "Validácia",
    color: "info",
    Icon: FactCheckOutlinedIcon,
    line: "Chcem len skontrolovať",
    desc: "Zistím, či sú v dokumente citlivé údaje. Dokument sa NEzmení.",
  },
  {
    title: "Anonymizácia",
    color: "warning",
    Icon: VisibilityOffOutlinedIcon,
    line: "Chcem natrvalo skryť",
    desc: "Údaje sa začiernia navždy. Späť ich už NEzískate.",
  },
  {
    title: "Pseudonymizácia",
    color: "success",
    Icon: LockOutlinedIcon,
    line: "Chcem skryť, ale vedieť vrátiť",
    desc: "Údaje sa nahradia. Neskôr ich viete vrátiť späť.",
  },
];

const Section = ({ title, children, defaultExpanded = false }) => (
  <Accordion defaultExpanded={defaultExpanded} disableGutters elevation={0}
    sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, "&:before": { display: "none" }, mb: 1 }}>
    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{title}</Typography>
    </AccordionSummary>
    <AccordionDetails>
      <Box sx={{ "& p": { mb: 1 }, color: "text.secondary", lineHeight: 1.7 }}>{children}</Box>
    </AccordionDetails>
  </Accordion>
);

const ManualPage = () => {
  const { user } = useAuth();
  const pdfUrl = `${process.env.PUBLIC_URL || ""}/manuals/user_manual.pdf`;

  return (
    <>
    <Sidebar user={user} />
    <Container maxWidth="lg" sx={{ py: { xs: 2, md: 4 } }}>
      {/* Hlavička */}
      <Paper elevation={0} sx={{ p: { xs: 2, md: 3 }, borderRadius: 3, border: "1px solid", borderColor: "divider", mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, flexWrap: "wrap" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <MenuBookRoundedIcon color="primary" sx={{ fontSize: 32 }} />
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 800 }}>Príručka</Typography>
              <Typography variant="body2" color="text.secondary">
                Jednoducho vysvetlené, ako program používať.
              </Typography>
            </Box>
          </Box>
          <Button variant="outlined" startIcon={<PictureAsPdfIcon />} href={pdfUrl} target="_blank" rel="noopener" sx={{ borderRadius: 2, fontWeight: 600 }}>
            Stiahnuť ako PDF
          </Button>
        </Box>
      </Paper>

      {/* Čo program robí – jednoduchý úvod */}
      <Paper elevation={0} sx={{ p: { xs: 2, md: 3 }, borderRadius: 3, border: "1px solid", borderColor: "divider", mb: 3 }}>
        <Typography variant="body1" sx={{ mb: 1 }}>
          Tento program vám pomôže <b>schovať osobné a citlivé údaje</b> v dokumentoch (napríklad mená, adresy,
          rodné čísla, čísla účtov) skôr, než ich niekomu pošlete alebo zverejníte.
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Sú tri možnosti. Nižšie je jednoducho vysvetlené, kedy ktorú použiť.
        </Typography>
      </Paper>

      {/* Ktorý proces si vybrať – 3 karty */}
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>Ktorú možnosť si vybrať?</Typography>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" }, gap: 2, mb: 4 }}>
        {COMPARE.map((c) => (
          <Paper key={c.title} elevation={0}
            sx={(theme) => ({
              p: 2.5, borderRadius: 3, border: "1px solid",
              borderColor: alpha(theme.palette[c.color].main, 0.4),
              bgcolor: alpha(theme.palette[c.color].main, 0.05),
            })}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <c.Icon sx={{ color: `${c.color}.main` }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: `${c.color}.main` }}>{c.title}</Typography>
            </Box>
            <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>„{c.line}“</Typography>
            <Typography variant="body2" color="text.secondary">{c.desc}</Typography>
          </Paper>
        ))}
      </Box>

      {/* Grafické zobrazenie procesov – krok za krokom */}
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>Krok za krokom</Typography>
      <Stack spacing={2.5} sx={{ mb: 4 }}>
        <ProcessFlow
          title="Validácia – kontrola"
          subtitle="Iba skontroluje dokument. Nič v ňom nezmení."
          color="info"
          Icon={FactCheckOutlinedIcon}
          steps={VALIDATION_STEPS}
          note="Tip: Použite, keď si chcete byť istí, či dokument obsahuje citlivé údaje – ešte predtým, než ho pošlete."
        />
        <ProcessFlow
          title="Anonymizácia – natrvalo skryť"
          subtitle="Citlivé údaje sa začiernia a už sa nedajú vrátiť späť."
          color="warning"
          Icon={VisibilityOffOutlinedIcon}
          steps={ANON_STEPS}
          note="Pozor: Toto je nezvratné. Pôvodné údaje sa z hotového súboru už nedajú získať – preto si nechajte originál uložený."
        />
        <ProcessFlow
          title="Pseudonymizácia – skryť a vedieť vrátiť"
          subtitle="Údaje sa nahradia náhradnými a neskôr ich viete vrátiť. Funguje pre Word (.docx)."
          color="success"
          Icon={LockOutlinedIcon}
          steps={PSEUDO_STEPS}
          note="Tip: Hodí sa, keď potrebujete dokument dočasne „očistiť“ (napr. na ukážku), ale neskôr budete chcieť pôvodné údaje späť."
        />
      </Stack>

      <Divider sx={{ mb: 3 }} />

      {/* Podrobnejšie – jednoduchým jazykom */}
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>Časté otázky a podrobnosti</Typography>

      <Section title="Ako sa prihlásim?" defaultExpanded>
        <p>Pri prihlásení okrem mena a hesla zadáte aj <b>6-miestny kód</b> z aplikácie v telefóne (Google Authenticator alebo Microsoft Authenticator). Je to ochrana navyše, aby sa k vášmu kontu nedostal nikto cudzí.</p>
        <p>Prvýkrát si v telefóne naskenujete QR kód, ktorý vám konto „spáruje“. Potom už len pri každom prihlásení prepíšete aktuálny kód z aplikácie. Prihlasovacie údaje vám prišli e-mailom.</p>
      </Section>

      <Section title="Ako schovám údaje v dokumente? (anonymizácia)">
        <p><b>1.</b> V menu kliknite na <b>Anonymizácia</b> a presuňte súbor do veľkého poľa (alebo naň kliknite a vyberte súbor z počítača). Naraz môžete pridať aj viac súborov. Fungujú PDF, Word a obrázky (PNG, JPG).</p>
        <p><b>2.</b> Kliknite na <b>Anonymizovať súbory</b> a vyberte, čo sa má skryť – buď hotový profil, alebo si zaškrtnite konkrétne údaje.</p>
        <p><b>3.</b> Program údaje nájde a začierni. Hotový súbor si stiahnete. Má v názve slovo <b>„Anonymized“</b>.</p>
      </Section>

      <Section title="Čo je profil a ako si ho vytvorím?">
        <p>Profil je <b>uložené nastavenie</b>, čo všetko sa má skrývať – aby ste to nemuseli zakaždým klikať nanovo.</p>
        <p>Vytvoríte ho v časti <b>Anonymizačné profily → Nový profil</b>: dáte mu názov, krátky popis a zaškrtnete údaje, ktoré chcete skrývať. Pri každom detektore je ikonka <b>(i)</b> s vysvetlením, čo presne nájde.</p>
        <p>Môžete pridať aj <b>vlastné slová</b> (napríklad interné označenie „FF12.2“). Ak zapnete <b>„Ukladať do databázy“</b>, hotové súbory nájdete neskôr v časti Súbory.</p>
      </Section>

      <Section title="Kde nájdem hotové súbory neskôr?">
        <p>Ak ste v profile zapli ukladanie, súbory sú v časti <b>Anonymizované súbory</b>. Nájdete tam pôvodný aj hotový (anonymizovaný) súbor pohromade.</p>
        <p>Pri každom súbore môžete: <b>stiahnuť</b>, ešte ručne doupraviť, elektronicky podpísať alebo zmazať. Súbory sa dajú aj vyhľadávať podľa textu.</p>
      </Section>

      <Section title="Môžem niečo začierniť ručne sám?">
        <p>Áno. Pri súbore zvoľte <b>Manuálna anonymizácia</b> (alebo kliknite na ikonku oka). Zapne sa <b>kreslenie</b> – myškou označíte časti, ktoré chcete skryť.</p>
        <p>Keď máte označené, kliknite na <b>Aplikovať anonymizáciu</b> a program ich prekryje. Omyl vrátite tlačidlom <b>Späť</b>, všetko označené zmažete tlačidlom <b>Vymazať</b>. Počas kreslenia sa dokument neposúva – na posúvanie kreslenie vypnite.</p>
      </Section>

      <Section title="Čo je pseudonymizácia a kedy ju použiť?">
        <p>Pseudonymizácia údaje <b>nahradí za náhradné</b> (napríklad skutočné meno za iné). Dokument tak vyzerá normálne, ale neobsahuje skutočné údaje.</p>
        <p>Najväčšia výhoda: <b>dá sa vrátiť späť</b>. Keď neskôr nahráte takýto súbor do časti De-pseudonymizácia, program z neho spraví zase pôvodný. Hodí sa, keď údaje potrebujete len dočasne schovať.</p>
      </Section>

      <Section title="Čo je validácia (kontrola)?">
        <p>Validácia dokument <b>iba skontroluje</b> a ukáže, aké citlivé údaje v ňom sú a koľko ich je. <b>Nič nemení.</b></p>
        <p>Vďaka tomu sa rozhodnete, či dokument môžete poslať tak, alebo ho radšej najprv necháte skryť. Z výsledku kontroly viete rovno spustiť anonymizáciu alebo pseudonymizáciu.</p>
      </Section>

      <Box sx={{ mt: 3, display: "flex", gap: 1, flexWrap: "wrap" }}>
        <Chip label="ISIT CENZOR" size="small" variant="outlined" />
        <Chip label="www.cenzor.sk" size="small" variant="outlined" />
        <Chip label="Potrebujete pomôcť? info@isitslovakia.sk" size="small" variant="outlined" />
      </Box>
    </Container>
    </>
  );
};

export default ManualPage;
